// Renders an interleaved chat turn: text segments become ChatBubbles, runs
// of consecutive activity entries become a single TerminalBlock. Preserves
// the agent's natural [text → tool → text → tool → text] reasoning rhythm
// so each thought between tool calls reads as its own bubble instead of
// being concatenated into one giant block.

import { AskUserQuestionCard } from "@/components/AskUserQuestionCard";
import { ChatBubble } from "@/components/ChatBubble";
import { ImageGalleryCard } from "@/components/ImageGalleryCard";
import { ProductCatalogCard } from "@/components/ProductCatalogCard";
import { TerminalBlock } from "@/components/TerminalBlock";
import { groupForRender } from "@/lib/timeline";
import type { TimelineSegment } from "@/lib/types";

export function TimelineView({
  segments,
  live = false,
  streaming = false,
  onSendMessage,
  briefingId,
  savedCatalogIds,
  onCatalogSaved,
}: {
  segments: TimelineSegment[];
  /** History (false) renders text/tools without typing animations.
   * Live (true) animates tool-call lines as they arrive. */
  live?: boolean;
  /** When true, the trailing text segment shows the streaming cursor. */
  streaming?: boolean;
  /** Lets inline cards (ProductCatalogCard's Generate button, future
   * actionable artifacts) fire a programmatic chat message. Plumbed
   * down from IntakePane → here → the card. extraRefIds inject extra
   * image refs (e.g. xlsx-embedded hero photos) that flow to Luma
   * alongside any composer-attached refs. enforcedImageRefs FORCES
   * the generate_images tool to use that exact ref_id list regardless
   * of what the LLM passes — used by single-look generation where
   * the per-look refs must reach Luma deterministically. */
  onSendMessage?: (
    text: string,
    extraRefIds?: string[],
    enforcedImageRefs?: string[],
  ) => void;
  /** Required for catalogs to be saveable — passes through to
   * ProductCatalogCard. Without it the card hides the Save button. */
  briefingId?: string;
  /** Catalog ids already persisted on the briefing — used to render a
   * "Saved" checkmark instead of an active Save button. */
  savedCatalogIds?: Set<string>;
  /** Fires after a successful save so the parent can refetch the
   * briefing record. */
  onCatalogSaved?: () => void;
}) {
  const groups = groupForRender(segments);
  if (groups.length === 0) return null;

  const lastIdx = groups.length - 1;

  return (
    <div className="space-y-4">
      {groups.map((group, i) => {
        if (group.kind === "text") {
          const isTrailing = i === lastIdx;
          return (
            <ChatBubble
              key={`t-${i}`}
              role="assistant"
              streaming={streaming && isTrailing}
            >
              {group.text}
            </ChatBubble>
          );
        }
        if (group.kind === "ask_user_question") {
          return (
            <AskUserQuestionCard
              key={`q-${i}`}
              data={
                group.entry.data as {
                  qid: string;
                  questions: {
                    question: string;
                    options?: string[];
                    multi?: boolean;
                  }[];
                  thread_id: string;
                }
              }
            />
          );
        }
        if (group.kind === "image_gallery") {
          return (
            <ImageGalleryCard
              key={`g-${i}`}
              data={
                group.entry.data as {
                  prompt?: string;
                  aspect_ratio?: string;
                  ref_ids?: string[];
                  images?: {
                    url: string;
                    generation_id: string;
                    aspect_ratio?: string;
                  }[];
                }
              }
            />
          );
        }
        if (group.kind === "product_catalog") {
          const catalogData = group.entry.data as {
            id: string;
            title?: string;
            audience?: string | null;
            looks: {
              id: string;
              title: string;
              styling_notes?: string | null;
              hero_ref_id?: string | null;
              hero_url?: string | null;
              products: {
                brand: string | null;
                code?: string | null;
                color?: string | null;
                description?: string | null;
                category?: string | null;
                notes?: string | null;
                ref_id?: string | null;
                ref_url?: string | null;
              }[];
            }[];
          };
          return (
            <ProductCatalogCard
              key={`pc-${i}`}
              data={catalogData}
              onSendMessage={onSendMessage}
              briefingId={briefingId}
              alreadySaved={savedCatalogIds?.has(catalogData.id) ?? false}
              onSaved={onCatalogSaved}
            />
          );
        }
        return <TerminalBlock key={`r-${i}`} entries={group.entries} live={live} />;
      })}
    </div>
  );
}
